import type { SenderBalanceState } from "@/hooks/useSenderBalance";

function formatAmount(amount: number, currency?: string | null): string {
  const num = amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${num} ${currency}` : num;
}

function formatCheckedAt(iso?: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

/** 상단 바에 표시하는 컴팩트 잔액 인디케이터 (클릭 시 새로고침) */
export function SenderBalanceIndicator({ state }: { state: SenderBalanceState }) {
  const { amount, data, loading, error, isCritical, isLow, refresh } = state;

  const tone = error
    ? "text-red-400"
    : isCritical
    ? "text-red-400"
    : isLow
    ? "text-amber-400"
    : "text-emerald-400";

  const label =
    loading && !data
      ? "확인 중"
      : error
      ? "확인 실패"
      : amount !== null
      ? formatAmount(amount, data?.currency)
      : "—";

  return (
    <button
      onClick={refresh}
      title="발신 계정 잔액 새로고침"
      className="flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] rounded-lg px-3 h-9 cursor-pointer transition-colors whitespace-nowrap"
    >
      <div className="w-4 h-4 flex items-center justify-center">
        {loading && !data ? (
          <i className="ri-loader-4-line animate-spin text-sm text-gray-400"></i>
        ) : (
          <i className={`ri-wallet-3-line text-sm ${tone}`}></i>
        )}
      </div>
      <span className="text-gray-500 text-xs">발신 잔액</span>
      <span className={`text-xs font-semibold ${tone}`}>{label}</span>
    </button>
  );
}

/** 잔액이 0이거나 부족할 때 상단에 뜨는 경고 배너 */
export function SenderBalanceBanner({ state }: { state: SenderBalanceState }) {
  const { amount, data, error, isCritical, isLow, refresh } = state;

  if (!isCritical && !isLow) return null;

  const isFallbackKey = data ? data.envKeyConfigured === false : false;

  const wrapperClass = isCritical
    ? "bg-red-500/10 border-red-500/30"
    : "bg-amber-500/10 border-amber-500/30";
  const iconClass = isCritical ? "text-red-400" : "text-amber-400";
  const titleClass = isCritical ? "text-red-300" : "text-amber-300";

  return (
    <div className={`mx-6 mt-6 rounded-2xl border px-5 py-4 ${wrapperClass}`}>
      <div className="flex items-start gap-3.5">
        <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-white/[0.06] shrink-0 ${iconClass}`}>
          <i className={`${isCritical ? "ri-error-warning-line" : "ri-alert-line"} text-xl`}></i>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm font-bold ${titleClass}`}>
              {isCritical ? "발신 계정 잔액이 0입니다" : "발신 계정 잔액이 부족합니다"}
            </p>
            {amount !== null && (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                isCritical ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"
              }`}>
                현재 {formatAmount(amount, data?.currency)}
              </span>
            )}
          </div>

          <p className="text-gray-400 text-xs leading-relaxed mt-1.5">
            {isCritical
              ? "문자 발송 대행 계정의 잔액이 소진되어 지금은 모든 발송이 실패합니다. 발신 계정을 충전한 뒤 다시 발송해 주세요."
              : `잔액이 ${formatAmount(5, data?.currency)} 이하로 떨어졌습니다. 잔액이 0이 되면 발송이 전부 실패하니 미리 충전을 권장합니다.`}
          </p>

          <div className="flex items-center gap-3 flex-wrap mt-2">
            {isFallbackKey && (
              <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                <i className="ri-key-2-line text-xs"></i>
                환경변수 키 미설정 · 예비 키로 조회 중
              </span>
            )}
            {data?.activeKeySource && !isFallbackKey && (
              <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
                <i className="ri-shield-check-line text-xs text-emerald-400"></i>
                운영 환경변수 키 사용 중
              </span>
            )}
            {data?.checkedAt && (
              <span className="flex items-center gap-1.5 text-[11px] text-gray-600">
                <i className="ri-time-line text-xs"></i>
                {formatCheckedAt(data.checkedAt)} 기준
              </span>
            )}
            {error && (
              <span className="flex items-center gap-1.5 text-[11px] text-red-400">
                <i className="ri-close-circle-line text-xs"></i>
                {error}
              </span>
            )}
          </div>
        </div>

        <button
          onClick={refresh}
          className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/[0.08] hover:bg-white/[0.14] text-gray-200 text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
        >
          <i className="ri-refresh-line text-sm"></i>
          다시 확인
        </button>
      </div>
    </div>
  );
}